import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  DataTable,
  Spinner,
  Banner,
  Checkbox,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface Plan {
  id: number;
  code: string;
  name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number;
  max_orders_per_month: number | null;
  max_warehouses: number | null;
  max_users: number | null;
  features: string;
  is_active: number;
  sort_order: number;
  subscriber_count?: number;
  mrr_contribution?: number;
}

export default function Plans() {
  const { fetchApi, error } = useApi();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriceMonthly, setEditPriceMonthly] = useState('');
  const [editPriceYearly, setEditPriceYearly] = useState('');
  const [editMaxOrders, setEditMaxOrders] = useState('');
  const [editMaxWarehouses, setEditMaxWarehouses] = useState('');
  const [editMaxUsers, setEditMaxUsers] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    const response = await fetchApi<{ success: boolean; plans: Plan[] }>('/super-admin/plans');
    if (response?.success) {
      setPlans(response.plans);
    }
    setLoading(false);
  }

  function openEditModal(plan: Plan) {
    setSelectedPlan(plan);
    setEditName(plan.name);
    setEditDescription(plan.description || '');
    setEditPriceMonthly(plan.price_monthly.toString());
    setEditPriceYearly(plan.price_yearly.toString());
    setEditMaxOrders(plan.max_orders_per_month?.toString() || '');
    setEditMaxWarehouses(plan.max_warehouses?.toString() || '');
    setEditMaxUsers(plan.max_users?.toString() || '');
    setEditIsActive(plan.is_active === 1);
    setSaveError(null);
    setEditModalOpen(true);
  }

  async function handleSavePlan() {
    if (!selectedPlan) return;

    setSaving(true);
    setSaveError(null);

    const response = await fetchApi<{ success: boolean; error?: string }>(
      `/super-admin/plans/${selectedPlan.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          name: editName.trim(),
          description: editDescription.trim() || null,
          price_monthly: parseFloat(editPriceMonthly) || 0,
          price_yearly: parseFloat(editPriceYearly) || 0,
          max_orders_per_month: editMaxOrders ? parseInt(editMaxOrders, 10) : null,
          max_warehouses: editMaxWarehouses ? parseInt(editMaxWarehouses, 10) : null,
          max_users: editMaxUsers ? parseInt(editMaxUsers, 10) : null,
          is_active: editIsActive ? 1 : 0,
        }),
      }
    );

    setSaving(false);

    if (response?.success) {
      setEditModalOpen(false);
      loadPlans();
    } else {
      setSaveError(response?.error || 'Failed to update plan');
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatLimit = (value: number | null) => {
    if (value === null) return 'Unlimited';
    return value.toLocaleString();
  };

  const rows = plans.map((plan) => [
    <div key={plan.id}>
      <Text as="span" fontWeight="semibold">{plan.name}</Text>
      <br />
      <Text as="span" tone="subdued" variant="bodySm">{plan.code}</Text>
    </div>,
    <BlockStack key={`price-${plan.id}`} gap="100">
      <Text as="span">{formatCurrency(plan.price_monthly)}/mo</Text>
      <Text as="span" tone="subdued" variant="bodySm">
        {formatCurrency(plan.price_yearly)}/yr
      </Text>
    </BlockStack>,
    <BlockStack key={`limits-${plan.id}`} gap="100">
      <Text as="span" variant="bodySm">{formatLimit(plan.max_orders_per_month)} orders</Text>
      <Text as="span" variant="bodySm">{formatLimit(plan.max_warehouses)} warehouses</Text>
      <Text as="span" variant="bodySm">{formatLimit(plan.max_users)} users</Text>
    </BlockStack>,
    plan.is_active ? (
      <Badge key={`status-${plan.id}`} tone="success">Active</Badge>
    ) : (
      <Badge key={`status-${plan.id}`} tone="critical">Inactive</Badge>
    ),
    <BlockStack key={`stats-${plan.id}`} gap="100">
      <Text as="span">{plan.subscriber_count || 0} shops</Text>
      <Text as="span" tone="subdued" variant="bodySm">
        {formatCurrency(plan.mrr_contribution || 0)} MRR
      </Text>
    </BlockStack>,
    <Button key={`edit-${plan.id}`} onClick={() => openEditModal(plan)}>
      Edit
    </Button>,
  ]);

  return (
    <Page title="Subscription Plans" subtitle="Manage pricing and plan limits">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Pricing Information</Text>
                <Text as="p" tone="subdued">
                  Edit plan pricing and limits below. Changes will affect new subscriptions
                  immediately. Existing subscribers will continue on their current terms until
                  renewal.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text']}
                headings={['Plan', 'Price', 'Limits', 'Status', 'Subscribers', 'Actions']}
                rows={rows}
              />
            )}
          </Card>
        </Layout.Section>

        {/* MRR Summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Revenue Summary</Text>
              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg">
                    {formatCurrency(plans.reduce((sum, p) => sum + (p.mrr_contribution || 0), 0))}
                  </Text>
                  <Text as="p" tone="subdued">Total MRR</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg">
                    {plans.reduce((sum, p) => sum + (p.subscriber_count || 0), 0)}
                  </Text>
                  <Text as="p" tone="subdued">Total Subscribers</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg">
                    {formatCurrency(
                      plans.reduce((sum, p) => sum + (p.mrr_contribution || 0), 0) /
                      Math.max(plans.reduce((sum, p) => sum + (p.subscriber_count || 0), 0), 1)
                    )}
                  </Text>
                  <Text as="p" tone="subdued">Avg Revenue per Shop</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Plan Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Edit ${selectedPlan?.name || 'Plan'}`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSavePlan,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {saveError && <Banner tone="critical">{saveError}</Banner>}

            <TextField
              label="Plan Name"
              requiredIndicator value={editName}
              onChange={setEditName}
              autoComplete="off"
            />

            <TextField
              label="Description"
              value={editDescription}
              onChange={setEditDescription}
              multiline={3}
              autoComplete="off"
            />

            <InlineStack gap="400">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Monthly Price (EUR)"
                  type="number"
                  requiredIndicator value={editPriceMonthly}
                  onChange={setEditPriceMonthly}
                  prefix="€"
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Yearly Price (EUR)"
                  type="number"
                  requiredIndicator value={editPriceYearly}
                  onChange={setEditPriceYearly}
                  prefix="€"
                  helpText="Usually ~17% discount"
                  autoComplete="off"
                />
              </div>
            </InlineStack>

            <Text as="h3" variant="headingSm">Plan Limits</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Leave empty for unlimited
            </Text>

            <InlineStack gap="400">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Max Orders/Month"
                  type="number"
                  requiredIndicator value={editMaxOrders}
                  onChange={setEditMaxOrders}
                  placeholder="Unlimited"
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Max Warehouses"
                  type="number"
                  requiredIndicator value={editMaxWarehouses}
                  onChange={setEditMaxWarehouses}
                  placeholder="Unlimited"
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Max Users"
                  type="number"
                  requiredIndicator value={editMaxUsers}
                  onChange={setEditMaxUsers}
                  placeholder="Unlimited"
                  autoComplete="off"
                />
              </div>
            </InlineStack>

            <Checkbox
              label="Plan is active and available for new subscriptions"
              checked={editIsActive}
              onChange={setEditIsActive}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
